const MVCObject = require("../../mvcobject").MVCObject;
const utils = require("../utils");
const getPolyline = require("../polyline");

/**
 * Direction Renderer for Baidu
 * @private
 */
class BaiduDirectionsRenderer extends MVCObject {
    /**
     * constructor
     * @param {{directions, routeIndex, panel}} params
     */
    constructor(params) {
        super();

        /**
         * the polyline to show on the map
         * @type {BaiduPolyline}
         */
        this.polyline = null;

        /**
         * map element to work with
         * @type {BMap.Map}
         */
        this.map = null;

        /**
         * data from Baidu API representing directions
         * @type {BMap.DrivingRouteResult}
         */
        this.directions = params.directions;

        /**
         * index of the linked route
         * @type {*|number|int}
         */
        this.routeIndex = params.routeIndex;

        /**
         * Element in wich add step details
         * @type {HTMLElement}
         */
        this.panel = params.panel;

        /**
         * Renderer options
         * @type {null|{suppressMarkers, suppressInfoWindows, polylineOptions}}
         */
        this.options = null;
    }

    /**
     * Applies Baidu polyline options
     * @private
     */
    _applyPolylineOptions() {
        if (
            this.polyline === null ||
            typeof this.options === "undefined" ||
            typeof this.options.polylineOptions === "undefined"
        ) {
            return;
        }

        this.polyline.setOptions(this.options.polylineOptions);
    }

    /**
     * Set renderer options
     * @param {{suppressMarkers, suppressInfoWindows, polylineOptions}} options Renderer options
     */
    setOptions(options) {
        this.options = options;
        this._applyPolylineOptions();
    }

    /**
     * Gets directions data.
     * @returns {directions|{routes}|{routes, request, status}|*}
     */
    getDirections() {
        return this.directions;
    }

    /**
     * Returns linked baidu map object.
     * @returns {BMap.Map}
     */
    getMap() {
        return this.map;
    }

    /**
     * Returns linked HTML panel.
     * @returns {HTMLElement}
     */
    getPanel() {
        return this.panel;
    }

    /**
     * Returns renderer route index.
     * @returns {number}
     */
    getRouteIndex() {
        return this.routeIndex;
    }

    /**
     * Sets directions
     * @param {BMap.DrivingRouteResult} directions
     */
    setDirections(directions) {
        this.directions = directions;
    }

    /**
     * Sets baidu map object, draw polyline and set viewport around it.
     * @param {BMap.Map} map
     */
    setMap(map) {
        // if map not set or unset remove polyline from map and exit
        if (typeof map === "undefined" || map === null) {
            if (this.map !== null) {
                this.polyline.setMap(null);
            }
            return;
        }

        //otherwise we need to show the polyline on the map
        this.map = map;

        // prepare list of all points
        const allPoints = [];

        /**
         * Adds a polyline overlay
         * @param {BaiduDirectionsRoute} route
         * @private
         */
        const _drawPolyline = (route, item) => {
            try {
                // get the polyline
                this.polyline = getPolyline({ path: route.getPath() });
                this._applyPolylineOptions();

                // add the polyline to the map
                this.polyline.setMap(map);

                // feed list of points
                const bounds = route.getBounds();
                allPoints.push(bounds.getNorthEast());
                allPoints.push(bounds.getSouthWest());
            } catch (e) {
                console.error(e);
            }
        };
        this.directions.routes.forEach(_drawPolyline);

        this.map.setViewport(allPoints);
    }

    /**
     * Sets HTMLElement as panel to show route steps.
     * @param {HTMLElement} panel
     */
    setPanel(panel) {
        if (typeof panel === "undefined" || panel === null) {
            if (this.panel !== null) {
                while (this.panel.hasChildNodes()) {
                    this.panel.removeChild(this.panel.lastChild);
                }
            }
            this.panel = null;

            return;
        }

        this.panel = panel;

        /**
         * Appends steps to panel
         * @param {BaiduDirectionsRoute} route
         * @private
         */
        const _appendSteps = (route) => {
            let i = 0;
            // we use legs[0] because waypoints are not actually used
            route.legs[0].steps.forEach((step) => {
                const htmlElement = document.createElement("div");
                htmlElement.innerHTML = step.getDescription(true);
                htmlElement.setAttribute("data-step-index", String(i));
                this.panel.appendChild(htmlElement);
                i++;
            });
        };
        this.directions.routes.forEach(_appendSteps);
    }

    /**
     * Sets renderer route index.
     * @param {int} index
     */
    setRouteIndex(index) {
        this.routeIndex = index;
    }
}

/**
 * @private
 * @param {google.maps.DirectionsRendererOptions|{directions, routeIndex, panel}} params
 * @returns {google.maps.DirectionsRenderer|BaiduDirectionsRenderer}
 */
function getDirectionsRenderer(params) {
    if (utils.isBaidu()) {
        return new BaiduDirectionsRenderer(params);
    }
    return new google.maps.DirectionsRenderer(params);
}

module.exports = getDirectionsRenderer;
